import { AgGridAngular } from "@ag-grid-community/angular";
import { GridOptions } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from "@angular/core";
import { DspAdgroup, DspAdgroupStateEnum, DspDeliveryStatus, DspStats } from "@front/m19-api-client";
import { IButtonDirective, ITooltipDirective, MODAL_DATA, ModalOptions, ModalRef } from "@front/m19-ui";
import { sumDspStats } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { restoreDefaultColumns } from "@m19-board/grid-config/grid-config";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { DspGridColumnsService, dspGridOptions } from "./dsp-grid-columns.service";

@Component({
  selector: "app-dsp-stats-line-item-details-modal",
  template: `
    <div *transloco="let t">
      <div class="mb-2 flex w-full items-center justify-end gap-1">
        <button
          iButton
          variant="secondary"
          size="md"
          [iTooltip]="t('common.restore_default_columns')"
          (click)="restoreDefaultColumns()"
        >
          {{ t("common.restore_columns") }}
        </button>
        <app-export-button [tooltipValue]="t('common.export_as_csv')" (export)="exportGridCsv()" />
      </div>
      <div class="ag-theme-quartz grid">
        <ag-grid-angular
          #grid
          [rowData]="rowData"
          [gridOptions]="gridOptions"
          [animateRows]="true"
          class="h-[40vh] w-full"
        >
        </ag-grid-angular>
      </div>
    </div>
  `,
  imports: [IButtonDirective, ITooltipDirective, TranslocoDirective, ExportButtonComponent, AgGridAngular],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspStatsLineItemDetailsModalComponent implements OnInit {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "xl" };

  private readonly dspColumns = inject(DspGridColumnsService);
  private readonly modalData = inject<DspStatsLineItemDetailsModalData>(MODAL_DATA);
  modalRef = inject(ModalRef);

  rowData: DspLineItemStats[] = [];
  readonly gridOptions: GridOptions<DspLineItemStats> = {
    ...dspGridOptions<DspLineItemStats>({ gridKey: "dsp_adgroup_stats", autoSizeDefault: true }),
    columnDefs: [
      this.dspColumns.lineItemNameColumn<DspLineItemStats>(),
      this.dspColumns.lineItemStateColumn<DspLineItemStats>(),
      this.dspColumns.deliveryStatusColumn<DspLineItemStats>(),
      ...this.dspColumns.metricsColumns<DspLineItemStats>(),
      this.dspColumns.currencyColumn<DspLineItemStats>(),
    ],
  };

  @ViewChild("grid") grid!: AgGridAngular;

  ngOnInit(): void {
    const dataPerLineItems = new Map<string, DspLineItemStats>();
    for (const stat of this.modalData.lineItemsData) {
      if (!stat.lineItemId) {
        continue;
      }
      if (!dataPerLineItems.has(stat.lineItemId)) {
        const lineItem = this.modalData.lineItems.get(stat.lineItemId);
        if (!lineItem) {
          continue;
        }
        dataPerLineItems.set(stat.lineItemId, {
          ...stat,
          name: lineItem.name,
          state: lineItem.state,
          deliveryStatus: lineItem.status?.deliveryStatus,
        });
      } else {
        const existing = dataPerLineItems.get(stat.lineItemId);
        if (existing) {
          dataPerLineItems.set(stat.lineItemId, {
            ...existing,
            ...sumDspStats(existing, stat),
          });
        }
      }
    }
    this.rowData = Array.from(dataPerLineItems.values());
  }

  close() {
    this.modalRef.close();
  }

  restoreDefaultColumns() {
    restoreDefaultColumns("dsp_adgroup_stats", this.grid.api);
  }

  exportGridCsv() {
    this.dspColumns.exportCsv(this.grid.api, {
      fileName: "dsp_line_item_stats",
      columnHeaders: {
        name: "dsp-stats.line_item",
        state: "dsp-stats.state",
        deliveryStatus: "dsp-stats.deliveryStatus",
      },
    });
  }
}

type DspLineItemStats = DspStats & {
  name?: string;
  state?: DspAdgroupStateEnum;
  deliveryStatus?: DspDeliveryStatus;
};

export interface DspStatsLineItemDetailsModalData {
  lineItemsData: DspStats[];
  lineItems: Map<string, DspAdgroup>;
}
